/**
 * Quick tender fills for Floor cash — Exact + next bills — plan/27, 35.
 */
import { d, moneyDisplay } from "@icraftx/shared/v1";

/** Common PH bill faces cashiers hand over (single-note tenders). */
export const POS_TENDER_BILLS = [20, 50, 100, 200, 500, 1000, 2000] as const;

export type TenderQuickFill = {
  /** Chip label (Exact or ₱100). */
  label: string;
  /** Display money string for the tender field (2 dp). */
  amount: string;
};

/**
 * Exact (= due) plus the next distinct bill faces ≥ due.
 * Empty when due is not a positive finite amount.
 */
export function suggestedTenderFills(
  dueStr: string,
  bills: readonly number[] = POS_TENDER_BILLS,
  max = 5,
): TenderQuickFill[] {
  const raw = dueStr.trim();
  if (!raw) return [];
  let due;
  try {
    due = d(raw);
  } catch {
    return [];
  }
  if (!due.isFinite() || due.lte(0)) return [];

  const dueMoney = moneyDisplay(due);
  const out: TenderQuickFill[] = [{ label: "Exact", amount: dueMoney }];
  const seen = new Set<string>([dueMoney]);

  for (const bill of bills) {
    if (out.length >= max) break;
    const face = d(bill);
    if (!face.isFinite() || face.lt(due)) continue;
    const amt = moneyDisplay(face);
    if (seen.has(amt)) continue;
    seen.add(amt);
    out.push({ label: `₱${bill}`, amount: amt });
  }

  return out;
}

/**
 * Cash-like methods get denomination chips; card / e-wallet stay Exact-only.
 * Prefer seeded `code` (cash / card / gcash / bank); name is fallback.
 */
export function payMethodUsesCashBills(input: {
  code?: string | null;
  name?: string | null;
}): boolean {
  const code = (input.code ?? "").trim().toLowerCase();
  if (code === "cash") return true;
  if (code === "card" || code === "gcash" || code === "bank") return false;
  if (code) return false;

  const name = (input.name ?? "").trim().toLowerCase();
  if (!name) return true;
  if (name === "gcash") return false;
  return name === "cash";
}
